import { useState, useEffect, type FormEvent } from 'react';
import { getCompanies, updateCompany } from '../api/companies';
import { getCredentials, upsertCredential, deleteCredential, getDiagnostics } from '../api/integrations';
import { getBots, createBot, updateBot, deleteBot } from '../api/bots';
import { attachWahaConnection, connectWaha, createWahaConnection, getWahaConnections, getWahaStatus, stopWaha, syncWahaWebhook, updateWahaConnection } from '../api/waha';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../layout/Topbar';
import type { Company, IntegrationCredential, CompanyBot, DiagnosticsResult, WahaConnection } from '../types';

export function SettingsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [credentials, setCredentials] = useState<IntegrationCredential[]>([]);
  const [bots, setBots] = useState<CompanyBot[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticsResult[]>([]);
  const [wahaConnections, setWahaConnections] = useState<WahaConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'integrations' | 'telegram' | 'whatsapp' | 'ai' | 'diagnostics'>('diagnostics');

  // AI config state
  const [aiCompanyId, setAiCompanyId] = useState('');
  const [aiOps, setAiOps] = useState<Record<string, boolean>>({
    invoiceLookup: true, contractLookup: true, serviceStatusLookup: true,
    invoiceSecondCopy: false, invoicePix: false, invoiceSend: false,
    ticketCreate: false, cpeReboot: false, radiusDisconnect: false,
  });
  const [aiSaving, setAiSaving] = useState(false);

  // Form states
  const [credProvider, setCredProvider] = useState('SGP');
  const [credCompanyId, setCredCompanyId] = useState('');
  const [credBaseUrl, setCredBaseUrl] = useState('');
  const [credApiKey, setCredApiKey] = useState('');
  const [credUsername, setCredUsername] = useState('');
  const [credPassword, setCredPassword] = useState('');
  const [credIsActive, setCredIsActive] = useState(true);
  const [selectedCredId, setSelectedCredId] = useState<string | null>(null);

  const [botToken, setBotToken] = useState('');
  const [botUsername, setBotUsername] = useState('');
  const [botCompanyId, setBotCompanyId] = useState('');
  const [botIsActive, setBotIsActive] = useState(true);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);

  const [wahaCompanyId, setWahaCompanyId] = useState('');
  const [wahaBaseUrl, setWahaBaseUrl] = useState('https://evolution.autegra.com.br');
  const [wahaApiKey, setWahaApiKey] = useState('');
  const [wahaInstanceName, setWahaInstanceName] = useState('');
  const [wahaAttachExisting, setWahaAttachExisting] = useState(false);
  const [wahaBusy, setWahaBusy] = useState(false);
  const [wahaStatuses, setWahaStatuses] = useState<Record<string, string>>({});
  const [wahaQrcodes, setWahaQrcodes] = useState<Record<string, string>>({});

  useEffect(() => {
    getCompanies().then(setCompanies).catch(() => console.error('Falha ao carregar empresas'));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [creds, bts, diag, evo] = await Promise.all([
        getCredentials().catch(() => { console.error('Falha ao carregar credenciais'); return []; }),
        getBots().catch(() => { console.error('Falha ao carregar bots'); return []; }),
        getDiagnostics().catch(() => { console.error('Falha ao carregar diagnóstico'); return []; }),
        getWahaConnections().catch(() => { console.error('Falha ao carregar WAHA'); return []; }),
      ]);
      setCredentials(creds);
      setBots(bts);
      setDiagnostics(diag);
      setWahaConnections(evo);
    } catch (err) { console.error('Falha ao carregar configurações:', err); } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (!aiCompanyId && companies.length > 0) setAiCompanyId(companies[0].id);
    if (!wahaCompanyId && companies.length > 0) setWahaCompanyId(companies[0].id);
  }, [companies]);

  const qrSource = (value: unknown): string => {
    if (typeof value === 'string') return value.startsWith('data:') ? value : value.length > 200 ? `data:image/png;base64,${value}` : '';
    if (value && typeof value === 'object') {
      const item = value as Record<string, unknown>;
      return qrSource(item.base64 ?? item.data ?? item.qrcode ?? item.code);
    }
    return '';
  };

  const handleWahaCreate = async (e: FormEvent) => {
    e.preventDefault();
    setWahaBusy(true);
    try {
      const created = wahaAttachExisting
        ? await attachWahaConnection({ companyId: wahaCompanyId, apiBaseUrl: wahaBaseUrl, apiKey: wahaApiKey, instanceName: wahaInstanceName })
        : await createWahaConnection({ companyId: wahaCompanyId, apiBaseUrl: wahaBaseUrl, apiKey: wahaApiKey, instanceName: wahaInstanceName });
      const qr = qrSource('qrcode' in created ? created.qrcode : null);
      if (qr) setWahaQrcodes((current) => ({ ...current, [created.id]: qr }));
      setWahaStatuses((current) => ({ ...current, [created.id]: created.connectionState || 'connecting' }));
      setWahaApiKey('');
      setWahaInstanceName('');
      addToast('success', wahaAttachExisting ? 'Instância existente vinculada e webhook configurado' : 'Instância WAHA criada e webhook configurado');
      await loadAll();
    } catch (error) {
      addToast('error', error instanceof Error ? error.message : 'Falha ao criar instância WAHA');
    } finally { setWahaBusy(false); }
  };

  const handleWahaStatus = async (connection: WahaConnection) => {
    try {
      const result = await getWahaStatus(connection.id);
      setWahaStatuses((current) => ({ ...current, [connection.id]: result.state }));
    } catch (error) { addToast('error', error instanceof Error ? error.message : 'Falha ao consultar conexão'); }
  };

  const handleWahaConnect = async (connection: WahaConnection) => {
    setWahaBusy(true);
    try {
      const result = await connectWaha(connection.id);
      const qr = qrSource(result.qrcode);
      if (qr) setWahaQrcodes((current) => ({ ...current, [connection.id]: qr }));
      setWahaStatuses((current) => ({ ...current, [connection.id]: result.state || 'connecting' }));
      addToast('success', qr ? 'QR Code atualizado' : 'Conexão consultada');
    } catch (error) { addToast('error', error instanceof Error ? error.message : 'Falha ao gerar QR Code'); }
    finally { setWahaBusy(false); }
  };

  const handleWahaWebhook = async (connection: WahaConnection) => {
    try {
      await syncWahaWebhook(connection.id);
      addToast('success', 'Webhook WAHA sincronizado');
    } catch (error) { addToast('error', error instanceof Error ? error.message : 'Falha ao sincronizar webhook'); }
  };

  const handleWahaStop = async (connection: WahaConnection) => {
    try {
      const result = await stopWaha(connection.id);
      setWahaStatuses((current) => ({ ...current, [connection.id]: result.state || 'STOPPED' }));
      addToast('success', 'Sessão WAHA parada sem remover o vínculo');
    } catch (error) { addToast('error', error instanceof Error ? error.message : 'Falha ao parar sessão'); }
  };

  const handleWahaToggle = async (connection: WahaConnection) => {
    try {
      await updateWahaConnection(connection.id, { isActive: !connection.isActive });
      await loadAll();
      addToast('success', connection.isActive ? 'Conexão desativada' : 'Conexão ativada');
    } catch (error) { addToast('error', error instanceof Error ? error.message : 'Falha ao alterar conexão'); }
  };

  useEffect(() => {
    const comp = companies.find((c) => c.id === aiCompanyId);
    if (comp?.aiAllowedOperations && typeof comp.aiAllowedOperations === 'object') {
      setAiOps((prev) => ({ ...prev, ...(comp.aiAllowedOperations as Record<string, boolean>) }));
    }
  }, [aiCompanyId]);

  const handleAiOpsSave = async () => {
    if (!aiCompanyId) return;
    setAiSaving(true);
    try {
      await updateCompany(aiCompanyId, { aiAllowedOperations: aiOps } as any);
      addToast('success', 'Operações AI atualizadas');
    } catch {
      addToast('error', 'Falha ao salvar configurações AI');
    } finally {
      setAiSaving(false);
    }
  };

  // ── Integration CRUD ──

  const handleCredSelect = (cred: IntegrationCredential) => {
    setSelectedCredId(cred.id);
    setCredProvider(cred.provider);
    setCredCompanyId(cred.companyId);
    setCredBaseUrl(cred.baseUrl || '');
    setCredApiKey('');
    setCredUsername('');
    setCredPassword('');
    setCredIsActive(cred.isActive);
  };

  const handleCredNew = () => {
    setSelectedCredId(null);
    setCredProvider('SGP');
    setCredCompanyId(companies[0]?.id || '');
    setCredBaseUrl('');
    setCredApiKey('');
    setCredUsername('');
    setCredPassword('');
    setCredIsActive(true);
  };

  const handleCredSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (selectedCredId) {
        await upsertCredential({
          provider: credProvider,
          companyId: credCompanyId,
          baseUrl: credBaseUrl,
          apiKey: credApiKey || undefined,
          username: credUsername || undefined,
          password: credPassword || undefined,
          isActive: credIsActive,
        });
      } else {
        await upsertCredential({
          provider: credProvider,
          companyId: credCompanyId,
          baseUrl: credBaseUrl,
          apiKey: credApiKey,
          username: credUsername,
          password: credPassword || undefined,
          isActive: credIsActive,
        });
      }
      handleCredNew();
      loadAll();
    } catch (err) { console.error('Falha ao salvar credencial:', err); }
  };

  const handleCredDelete = async () => {
    if (!selectedCredId) return;
    try {
      await deleteCredential(selectedCredId);
      handleCredNew();
      loadAll();
    } catch (err) { console.error('Falha ao excluir credencial:', err); }
  };

  // ── Bot CRUD ──

  const handleBotSelect = (bot: CompanyBot) => {
    setSelectedBotId(bot.id);
    setBotUsername(bot.username);
    setBotCompanyId(bot.companyId);
    setBotIsActive(bot.isActive);
    setBotToken('');
  };

  const handleBotNew = () => {
    setSelectedBotId(null);
    setBotToken('');
    setBotUsername('');
    setBotCompanyId(companies[0]?.id || '');
    setBotIsActive(true);
  };

  const handleBotSave = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (selectedBotId) {
        await updateBot(selectedBotId, {
          username: botUsername,
          botToken: botToken || undefined,
          isActive: botIsActive,
        });
      } else {
        await createBot({ botToken, username: botUsername, companyId: botCompanyId });
      }
      handleBotNew();
      loadAll();
    } catch (err) { console.error('Falha ao salvar bot:', err); }
  };

  const handleBotDelete = async () => {
    if (!selectedBotId) return;
    try {
      await deleteBot(selectedBotId);
      handleBotNew();
      loadAll();
    } catch (err) { console.error('Falha ao excluir bot:', err); }
  };

  return (
    <div className="view-container">
      <Topbar title="Configuração" subtitle="Integrações, bots, WhatsApp e diagnósticos." />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {(['diagnostics', 'integrations', 'telegram', 'whatsapp', 'ai'] as const).map((tab) => (
          <button
            key={tab}
            className={activeTab === tab ? '' : 'secondary'}
            onClick={() => setActiveTab(tab)}
            type="button"
          >
            {{ diagnostics: '🔍 Diagnóstico', integrations: '🔌 Integrações', telegram: '🤖 Bots Telegram', whatsapp: '📱 WhatsApp', ai: '🧠 AI / Operações' }[tab]}
          </button>
        ))}
      </div>

      {/* ─── Diagnostics Tab ─── */}
      {activeTab === 'diagnostics' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <button onClick={loadAll} disabled={loading} style={{ width: 'auto' }}>
            {loading ? 'Atualizando...' : 'Atualizar diagnóstico'}
          </button>

          {diagnostics.length === 0 ? (
            <div className="panel">
              <div className="empty-state">
                <p>Nenhuma empresa configurada ou dados indisponíveis.</p>
              </div>
            </div>
          ) : (
            diagnostics.map((diag) => (
              <div key={diag.id} className="panel">
                <div className="panel-header">
                  <h2>{diag.name}</h2>
                  <span className={`pill ${diag.readyForTelegramSgp ? 'success' : 'warning'}`}>
                    {diag.readyForTelegramSgp ? 'Pronto' : 'Incompleto'}
                  </span>
                </div>
                <div className="panel-body">
                  <div style={{ marginBottom: 16 }}>
                    <strong style={{ fontSize: '0.857rem' }}>Bots Telegram</strong>
                    {diag.bots.length === 0 ? (
                      <p style={{ color: 'var(--text-tertiary)', fontSize: '0.857rem' }}>Nenhum bot ativo</p>
                    ) : (
                      diag.bots.map((bot) => (
                        <div key={bot.id} className="detail-row" style={{ padding: '4px 0' }}>
                          <span className="label">@{bot.username}</span>
                          <span className={`pill ${bot.isActive ? 'success' : 'neutral'}`}>
                            {bot.provider}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.857rem' }}>SGP</strong>
                    {diag.sgp.length === 0 ? (
                      <p style={{ color: 'var(--text-tertiary)', fontSize: '0.857rem' }}>Nenhuma credencial</p>
                    ) : (
                      diag.sgp.map((sgp) => (
                        <div key={sgp.id} className="detail-row" style={{ padding: '4px 0' }}>
                          <span className="label">{sgp.baseUrl || 'URL não configurada'}</span>
                          <span className={`pill ${sgp.ready ? 'success' : 'danger'}`}>
                            {sgp.ready ? 'OK' : 'Incompleto'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ─── Integrations Tab ─── */}
      {activeTab === 'integrations' && (
        <div className="records-layout">
          <div className="panel">
            <div className="panel-header">
              <h2>Credenciais</h2>
              <button onClick={handleCredNew}>+ Nova</button>
            </div>
            <div className="data-list">
              {credentials.length === 0 ? (
                <div className="empty-state"><p>Nenhuma credencial cadastrada.</p></div>
              ) : (
                credentials.map((cred) => (
                  <div
                    key={cred.id}
                    className="data-list-item"
                    onClick={() => handleCredSelect(cred)}
                    style={{ background: selectedCredId === cred.id ? 'var(--color-primary-bg)' : undefined }}
                  >
                    <div>
                      <div className="item-name">{cred.provider}</div>
                      <div className="item-detail">{cred.baseUrl || 'sem URL'}</div>
                    </div>
                    <span className={`pill ${cred.isActive ? 'success' : 'neutral'}`}>
                      {cred.isActive ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">
              <h2>{selectedCredId ? 'Editar' : 'Nova'} credencial</h2>
            </div>
            <div className="panel-body">
              <form className="form-stack" onSubmit={handleCredSave}>
                <label>
                  Provedor
                  <select value={credProvider} onChange={(e) => setCredProvider(e.target.value)}>
                    <option value="SGP">SGP</option>
                    <option value="RECEITANET">ReceitaNet</option>
                  </select>
                </label>
                <label>
                  Empresa
                  <select value={credCompanyId} onChange={(e) => setCredCompanyId(e.target.value)}>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label>
                  Base URL
                  <input value={credBaseUrl} onChange={(e) => setCredBaseUrl(e.target.value)} placeholder="https://seu-dominio.sgp.net.br" />
                </label>
                <label>
                  Token
                  <input value={credApiKey} onChange={(e) => setCredApiKey(e.target.value)} type="password" autoComplete="new-password" />
                </label>
                <label>
                  App
                  <input value={credUsername} onChange={(e) => setCredUsername(e.target.value)} autoComplete="off" />
                </label>
                <label>
                  Senha (opcional)
                  <input value={credPassword} onChange={(e) => setCredPassword(e.target.value)} type="password" autoComplete="new-password" />
                </label>
                <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={credIsActive} onChange={(e) => setCredIsActive(e.target.checked)} />
                  Ativa
                </label>
                <div className="flex gap-sm">
                  <button type="submit">Salvar</button>
                  <button className="secondary" onClick={handleCredNew} type="button">Cancelar</button>
                  {selectedCredId && <button className="danger" onClick={handleCredDelete} type="button">Excluir</button>}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ─── WhatsApp Bots Tab ─── */}
      {activeTab === 'whatsapp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="panel">
            <div className="panel-header">
              <h2>📱 WhatsApp Cloud API</h2>
              <span className="pill neutral">Meta Official</span>
            </div>
            <div className="panel-body">
              <p className="text-sm text-secondary" style={{ marginBottom: 12 }}>
                Conecte-se à API oficial do WhatsApp Business da Meta.
                Configure as credenciais no SGP e crie um bot com o provedor WhatsApp Cloud.
              </p>
              {bots.filter((b) => b.provider === 'WHATSAPP_CLOUD').length === 0 ? (
                <div className="empty-state" style={{ padding: 'var(--space-md)' }}>
                  <p>Nenhum bot WhatsApp Cloud configurado. Crie um bot pelo formulário abaixo.</p>
                </div>
              ) : (
                bots.filter((b) => b.provider === 'WHATSAPP_CLOUD').map((bot) => (
                  <div key={bot.id} className="detail-row" style={{ padding: '6px 0' }}>
                    <span className="label">📱 {bot.displayPhoneNumber || bot.username}</span>
                    <span className={`pill ${bot.isActive ? 'success' : 'neutral'}`}>
                      {bot.isActive ? 'Conectado' : 'Desconectado'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">
              <h2>⚡ WAHA API</h2>
              <span className="pill success">https://evolution.autegra.com.br</span>
            </div>
            <div className="panel-body">
              <p className="text-sm text-secondary" style={{ marginBottom: 12 }}>
                WAHA 2026.7.1 com WEBJS, sessão isolada por empresa e webhook HMAC-SHA512. Uma nova sessão é criada parada e só conecta quando você solicitar o QR Code.
              </p>
              <p className="text-sm" style={{ marginBottom: 12, color: 'var(--warning-color, #b45309)' }}>
                Durante a suspensão do número, não clique em “Iniciar e gerar QR Code”. O WAHA não elimina o risco de bloqueio por ser uma integração não oficial.
              </p>
              <form className="form-stack" onSubmit={handleWahaCreate} style={{ marginBottom: 20 }}>
                <label>
                  Empresa
                  <select value={wahaCompanyId} onChange={(e) => setWahaCompanyId(e.target.value)} required>
                    <option value="">Selecione</option>
                    {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                  </select>
                </label>
                <label>
                  URL da WAHA
                  <input value={wahaBaseUrl} onChange={(e) => setWahaBaseUrl(e.target.value)} placeholder="https://evolution.autegra.com.br" required />
                </label>
                <label>
                  API key global
                  <input value={wahaApiKey} onChange={(e) => setWahaApiKey(e.target.value)} type="password" autoComplete="new-password" placeholder="Usada apenas no provisionamento" required />
                </label>
                <label>
                  Nome da instância
                  <input value={wahaInstanceName} onChange={(e) => setWahaInstanceName(wahaAttachExisting ? e.target.value : e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder={wahaAttachExisting ? 'EVOLUIR TELECOM' : 'evoluir-fibra'} minLength={3} maxLength={80} required />
                </label>
                <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={wahaAttachExisting} onChange={(e) => { setWahaAttachExisting(e.target.checked); setWahaInstanceName(''); }} />
                  Vincular uma instância que já existe na WAHA
                </label>
                <button type="submit" disabled={wahaBusy || !wahaCompanyId || !wahaApiKey || !wahaInstanceName}>
                  {wahaBusy ? 'Configurando...' : wahaAttachExisting ? 'Vincular instância e configurar webhook' : 'Criar instância e webhook'}
                </button>
              </form>

              {wahaConnections.length === 0 ? (
                <div className="empty-state" style={{ padding: 'var(--space-md)' }}>
                  <p>Nenhuma conexão WAHA configurada.</p>
                </div>
              ) : (
                wahaConnections.map((connection) => (
                  <div key={connection.id} style={{ borderTop: '1px solid var(--border-color)', padding: '14px 0' }}>
                    <div className="detail-row" style={{ padding: '0 0 8px' }}>
                      <div>
                        <strong>⚡ {connection.instanceName || connection.username}</strong>
                        <div className="text-sm text-secondary">{companies.find((company) => company.id === connection.companyId)?.name || 'Empresa'} · {connection.apiBaseUrl}</div>
                      </div>
                      <span className={`pill ${wahaStatuses[connection.id] === 'WORKING' ? 'success' : connection.isActive ? 'warning' : 'neutral'}`}>
                        {!connection.isActive ? 'Inativa' : wahaStatuses[connection.id] || 'Status não consultado'}
                      </span>
                    </div>
                    <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
                      <button type="button" className="secondary" onClick={() => handleWahaStatus(connection)}>Verificar status</button>
                      <button type="button" onClick={() => handleWahaConnect(connection)} disabled={wahaBusy}>Iniciar e gerar QR Code</button>
                      <button type="button" className="secondary" onClick={() => handleWahaStop(connection)}>Parar sessão</button>
                      <button type="button" className="secondary" onClick={() => handleWahaWebhook(connection)}>Sincronizar webhook</button>
                      <button type="button" className="secondary" onClick={() => handleWahaToggle(connection)}>{connection.isActive ? 'Desativar' : 'Ativar'}</button>
                    </div>
                    {wahaQrcodes[connection.id] && (
                      <div style={{ marginTop: 14, textAlign: 'center' }}>
                        <img src={wahaQrcodes[connection.id]} alt={`QR Code da instância ${connection.instanceName}`} style={{ width: 260, maxWidth: '100%', background: '#fff', padding: 10, borderRadius: 8 }} />
                        <p className="text-sm text-secondary">Abra o WhatsApp no celular → Aparelhos conectados → Conectar aparelho.</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

        </div>
      )}

      {/* ─── Telegram Bots Tab ─── */}
      {activeTab === 'telegram' && (
        <div className="records-layout">
          <div className="panel">
            <div className="panel-header">
              <h2>Bots Telegram</h2>
              <button onClick={handleBotNew}>+ Novo</button>
            </div>
            <div className="data-list">
              {bots.filter((b) => b.provider === 'TELEGRAM').length === 0 ? (
                <div className="empty-state"><p>Nenhum bot Telegram cadastrado.</p></div>
              ) : (
                bots.filter((b) => b.provider === 'TELEGRAM').map((bot) => (
                  <div
                    key={bot.id}
                    className="data-list-item"
                    onClick={() => handleBotSelect(bot)}
                    style={{ background: selectedBotId === bot.id ? 'var(--color-primary-bg)' : undefined }}
                  >
                    <div>
                      <div className="item-name">@{bot.username}</div>
                      <div className="item-detail">
                        {companies.find((c) => c.id === bot.companyId)?.name || 'Sem empresa'}
                      </div>
                    </div>
                    <span className={`pill ${bot.isActive ? 'success' : 'neutral'}`}>
                      {bot.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">
              <h2>{selectedBotId ? 'Editar' : 'Novo'} bot</h2>
            </div>
            <div className="panel-body">
              <form className="form-stack" onSubmit={handleBotSave}>
                <label>
                  Token
                  <input value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder="Token do bot" />
                </label>
                <label>
                  Username
                  <input value={botUsername} onChange={(e) => setBotUsername(e.target.value)} placeholder="@seu_bot" />
                </label>
                <label>
                  Empresa
                  <select value={botCompanyId} onChange={(e) => setBotCompanyId(e.target.value)}>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={botIsActive} onChange={(e) => setBotIsActive(e.target.checked)} />
                  Ativo
                </label>
                <div className="flex gap-sm">
                  <button type="submit">Salvar</button>
                  <button className="secondary" onClick={handleBotNew} type="button">Cancelar</button>
                  {selectedBotId && <button className="danger" onClick={handleBotDelete} type="button">Excluir</button>}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* ─── AI / Allowed Operations Tab ─── */}
      {activeTab === 'ai' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="panel">
            <div className="panel-header">
              <h2>Operações permitidas para AI</h2>
              <span className="pill neutral">Automação por empresa</span>
            </div>
            <div className="panel-body">
              <p style={{ fontSize: '0.857rem', color: 'var(--text-secondary)', marginBottom: 12 }}>
                Configure quais operações SGP o assistente AI pode executar automaticamente via Telegram.
                Operações de leitura (consulta) ficam habilitadas por padrão. Operações de escrita exigem ativação explícita.
              </p>

              <label>
                Empresa
                <select value={aiCompanyId} onChange={(e) => setAiCompanyId(e.target.value)}>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>

              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: '0.786rem', color: 'var(--text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Consulta (leitura)
                </h4>
                {(['invoiceLookup', 'contractLookup', 'serviceStatusLookup'] as const).map((key) => (
                  <label key={key} className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <input type="checkbox" checked={!!aiOps[key]} onChange={(e) => setAiOps((p) => ({ ...p, [key]: e.target.checked }))} />
                    <span style={{ fontSize: '0.857rem' }}>
                      {{ invoiceLookup: 'Consulta de faturas', contractLookup: 'Consulta de contratos', serviceStatusLookup: 'Consulta de status de serviço' }[key]}
                    </span>
                  </label>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: '0.786rem', color: 'var(--text-tertiary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Escrita (ação)
                </h4>
                {(['invoiceSecondCopy', 'invoicePix', 'invoiceSend', 'ticketCreate', 'cpeReboot', 'radiusDisconnect'] as const).map((key) => (
                  <label key={key} className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                    <input type="checkbox" checked={!!aiOps[key]} onChange={(e) => setAiOps((p) => ({ ...p, [key]: e.target.checked }))} />
                    <span style={{ fontSize: '0.857rem' }}>
                      {{ invoiceSecondCopy: 'Gerar 2ª via de boleto', invoicePix: 'Gerar código PIX', invoiceSend: 'Enviar fatura por email/SMS', ticketCreate: 'Criar ticket no SGP', cpeReboot: 'Reiniciar CPE do cliente', radiusDisconnect: 'Desconectar sessão RADIUS' }[key]}
                    </span>
                  </label>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <button onClick={handleAiOpsSave} disabled={aiSaving || !aiCompanyId}>
                  {aiSaving ? 'Salvando...' : 'Salvar configurações'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
